// Launch Reset — Owner-only page that wipes transactional data before
// a Soft Launch. Two scopes (brand-wide or single branch), two toggles
// (transactions / customers), a manager re-authentication step and a
// verbatim confirmation phrase. Runs a dry-run preview before the
// destructive call so the operator sees exactly what will disappear.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { useAuth } from "@/lib/auth";
import { canAccessLaunchReset } from "@/lib/AuthEngine";
import { ReauthCodeFields } from "@/components/auth/ReauthCodeDialog";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, Loader2, CheckCircle2 } from "lucide-react";

const CONFIRMATION_TEXT = "WIPE COCOON NEST";

export default function LaunchReset() {
  const { staff } = useAuth();
  const nav = useNavigate();
  const [branches, setBranches] = useState([]);
  const [scope, setScope] = useState("branch");
  const [branchId, setBranchId] = useState("");
  const [wipeTxn, setWipeTxn] = useState(true);
  const [wipeCust, setWipeCust] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [pendingId, setPendingId] = useState("");
  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Only the Owner may load this page; if a non-Owner reaches it (e.g.
  // typed the URL) bounce them back to the brand dashboard. The gate
  // is resolved via AuthEngine so the same predicate powers the
  // sidebar Absolute-Hiding filter and the route guard.
  useEffect(() => {
    if (staff && !canAccessLaunchReset(staff)) {
      toast.error("Launch Reset is Owner-only.");
      nav("/staff/brand", { replace: true });
    }
  }, [staff, nav]);

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranches(r.data)).catch((err) => toast.error(formatApiError(err)));
  }, []);

  const runPreview = async () => {
    if (scope === "branch" && !branchId) {
      toast.error("Select a branch first.");
      return;
    }
    setPreviewing(true);
    setPreview(null);
    try {
      const params = { scope, wipe_transactions: wipeTxn, wipe_customers: wipeCust };
      if (scope === "branch") params.branch_id = branchId;
      const r = await staffApi.get("/admin/launch-reset/preview", { params });
      setPreview(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  // Whenever the operator toggles any of the four inputs that influence
  // the wipe — scope, target branch, "wipe transactions" or "wipe customer
  // profiles" — the dry-run count MUST re-run so the numbers on-screen
  // always reflect what the current selection would actually delete.
  // Without this, unticking Customer profiles left the customers row
  // stuck at "17 will be deleted" until the operator manually clicked
  // "Run preview" again (the bug that produced ghost-count screenshots).
  useEffect(() => {
    if (!preview) return;              // no live preview yet — skip
    if (!(wipeTxn || wipeCust)) { setPreview(null); return; }
    if (scope === "branch" && !branchId) return;
    runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, branchId, wipeTxn, wipeCust]);

  const phraseMatches = phrase.trim() === CONFIRMATION_TEXT;
  const somethingToWipe = wipeTxn || wipeCust;
  const canPreview = somethingToWipe && (scope === "brand" || branchId);
  const canArm = canPreview && preview && phraseMatches && pendingId && code.trim().length === 6;
  const custWipeDisabled = scope === "branch";

  useEffect(() => { if (custWipeDisabled && wipeCust) setWipeCust(false); }, [custWipeDisabled, wipeCust]);

  const totalPreview = preview ? preview.total_rows : 0;

  const branchLabel = useMemo(() => {
    if (scope !== "branch") return "brand-wide";
    return branches.find((b) => b.id === branchId)?.name || "—";
  }, [scope, branchId, branches]);

  const submitReset = async () => {
    setConfirmOpen(false);
    setRunning(true);
    setResult(null);
    try {
      const body = {
        scope,
        branch_id: scope === "branch" ? branchId : null,
        wipe_transactions: wipeTxn,
        wipe_customers: wipeCust,
        manager_pending_id: pendingId,
        manager_code: code.trim(),
        confirmation_text: phrase.trim(),
      };
      const r = await staffApi.post("/admin/launch-reset", body);
      setResult(r.data);
      toast.success(`Reset complete — ${r.data.total_deleted} rows removed.`);
      setPendingId("");
      setCode("");
      setPhrase("");
      setPreview(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <StaffShell context="brand">
      <div className="max-w-3xl">
        <div className="flex items-start gap-3 mb-6">
          <div className="shrink-0 h-11 w-11 rounded-md bg-rose-100 text-rose-700 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold leading-tight">Launch Reset</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Wipe transactional data before a Soft Launch. Services, packages, products, staff logins and roles are always preserved. This action <span className="font-semibold">cannot be undone</span>.
            </p>
          </div>
        </div>

        {/* Result banner (rendered after a successful wipe) */}
        {result && (
          <Card className="rounded-lg border-emerald-200 bg-emerald-50 mb-4" data-testid="reset-result-card">
            <CardContent className="p-5">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-semibold text-emerald-900">Reset complete · {result.total_deleted} row{result.total_deleted === 1 ? "" : "s"} deleted</h2>
                  <p className="text-xs text-emerald-800 mt-0.5">Scope: <span className="font-medium">{result.scope}</span>{result.branch_id ? ` · branch ${result.branch_id}` : ""}. Reports will now render zero until new activity is recorded.</p>
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-emerald-900 font-medium">Per-collection counts</summary>
                    <ul className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-emerald-900" data-testid="reset-result-counts">
                      {Object.entries(result.wiped).map(([k, v]) => (
                        <li key={k} className="flex justify-between"><span className="text-emerald-800">{k}</span><span className="font-mono">{v}</span></li>
                      ))}
                    </ul>
                  </details>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scope selection */}
        <Card className="rounded-lg mb-4" data-testid="reset-scope-card">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-sm font-medium">Reset scope</Label>
              <RadioGroup value={scope} onValueChange={setScope} className="mt-3 flex flex-col gap-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <RadioGroupItem value="branch" data-testid="scope-branch" />
                  <span className="text-sm">
                    <span className="font-medium">One location</span> — wipe only rows belonging to a single branch. Other branches' data is left intact (this is what you want for a Soft Launch).
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <RadioGroupItem value="brand" data-testid="scope-brand" />
                  <span className="text-sm">
                    <span className="font-medium">Brand-wide</span> — every location. Required if you also want to wipe customer profiles.
                  </span>
                </label>
              </RadioGroup>
            </div>

            {scope === "branch" && (
              <div>
                <Label className="text-sm">Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger className="w-full mt-2" data-testid="reset-branch-picker"><SelectValue placeholder="Pick a branch…" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* What to wipe */}
        <Card className="rounded-lg mb-4" data-testid="reset-toggles-card">
          <CardContent className="p-5 space-y-3">
            <Label className="text-sm font-medium">What to wipe</Label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={wipeTxn} onCheckedChange={(v) => setWipeTxn(!!v)} data-testid="toggle-wipe-txn" />
              <span className="text-sm">
                <span className="font-medium">Transactions</span> — orders, payments, appointments, medical records, refunds, POS approvals, audit trail, email dispatch log.
              </span>
            </label>
            <label className={`flex items-start gap-3 ${custWipeDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
              <Checkbox checked={wipeCust} onCheckedChange={(v) => setWipeCust(!!v)} disabled={custWipeDisabled} data-testid="toggle-wipe-cust" />
              <span className="text-sm">
                <span className="font-medium">Customer profiles</span> — every client, medical history, loyalty ledger, gift cards, series and pending registrations.
                {custWipeDisabled && <span className="block text-xs text-muted-foreground mt-0.5">Customer registry is brand-wide, so wiping it requires <span className="font-medium">Brand-wide</span> scope.</span>}
              </span>
            </label>
            <p className="text-xs text-muted-foreground pt-1">
              <span className="font-medium">Always preserved:</span> services, packages, products, series templates, staff logins, roles, categories, cancellation reasons, rooms, equipment, payment types, gift-card templates, email templates, brand settings, inventory stock levels.
            </p>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="rounded-lg mb-4" data-testid="reset-preview-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <Label className="text-sm font-medium">Preview — dry-run counts</Label>
                <p className="text-xs text-muted-foreground mt-0.5">See exactly what would be deleted before you type the confirmation phrase.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={runPreview}
                disabled={!canPreview || previewing}
                data-testid="reset-preview-btn"
              >
                {previewing ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Counting…</> : "Run preview"}
              </Button>
            </div>
            {preview && (
              <div className="mt-4 border rounded-md p-3 bg-secondary/30" data-testid="reset-preview-detail">
                <p className="text-sm font-semibold">Total rows: <span data-testid="preview-total-rows">{totalPreview}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">Scope: {preview.scope}{preview.branch_id ? ` · ${branchLabel}` : ""}</p>
                <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  {Object.entries(preview.counts).map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="font-mono">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirmation phrase + credentials */}
        <Card className={`rounded-lg mb-4 border-rose-200 ${phraseMatches ? "bg-rose-50/40" : ""}`} data-testid="reset-arm-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-700 mt-0.5 shrink-0" />
              <div>
                <Label className="text-sm font-medium">Arm the wipe</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Type <code className="bg-muted px-1 rounded">{CONFIRMATION_TEXT}</code> below, then confirm with the one-time code we email you. Both are required.</p>
              </div>
            </div>

            <div>
              <Label htmlFor="phrase" className="text-xs">Confirmation phrase</Label>
              <Input
                id="phrase"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={CONFIRMATION_TEXT}
                className="mt-1 font-mono"
                autoComplete="off"
                data-testid="reset-confirm-phrase"
              />
              {phrase && !phraseMatches && (
                <p className="text-xs text-rose-700 mt-1">Phrase doesn't match exactly.</p>
              )}
            </div>

            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground mb-2">
                Confirm with a one-time code emailed to <span className="font-medium text-foreground">{staff?.email || "your work email"}</span>.
              </p>
              <ReauthCodeFields
                purpose="launch_reset"
                email=""
                setEmail={() => {}}
                pendingId={pendingId}
                setPendingId={setPendingId}
                code={code}
                setCode={setCode}
                busy={codeBusy}
                setBusy={setCodeBusy}
                idPrefix="reset-owner"
              />
            </div>

            <Button
              variant="destructive"
              className="w-full h-11"
              disabled={!canArm || running}
              onClick={() => setConfirmOpen(true)}
              data-testid="reset-arm-btn"
            >
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wiping…</> : `Permanently wipe ${branchLabel}`}
            </Button>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="reset-final-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Final confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <span className="font-semibold">{totalPreview} row{totalPreview === 1 ? "" : "s"}</span> from <span className="font-semibold">{branchLabel}</span>.
              <br />
              This includes: {wipeTxn ? "all transactions" : ""}{wipeTxn && wipeCust ? " and " : ""}{wipeCust ? "every customer profile" : ""}.
              <br /><br />
              <span className="font-semibold text-rose-700">This cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-final-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitReset} className="bg-rose-700 hover:bg-rose-800" data-testid="reset-final-confirm">
              Yes, wipe everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </StaffShell>
  );
}
